import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState, useEffect } from "react";

import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import type { IUseTableSelectionButtons } from "pro-features/components/TableSelection/src/hooks/useTableSelectionButtons";
import { useTableSelectionButtons } from "pro-features/components/TableSelection/src/hooks/useTableSelectionButtons";
import { allTrackers } from "app/services/track/track";

import type { KeywordTransformedData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";

const MAX_DEFAULT_SELECTED_ROWS = 100;
const CLICKS_OVERTIME_ICON: IconName = "overview";

type WebsiteKeywordsPage2TableSelectionButtons = IUseTableSelectionButtons & {
    isGraphDisplayed: boolean;
    setIsGraphDisplayed: Dispatch<SetStateAction<boolean>>;
};

export const useWebsiteKeywordsPage2TableSelectionButtons = (
    setSelectedRows: Dispatch<SetStateAction<KeywordTransformedData[]>>,
    isLoading: boolean,
    data: KeywordTransformedData[] = [],
    selectedRowsLength: number,
): WebsiteKeywordsPage2TableSelectionButtons => {
    const [isGraphDisplayed, setIsGraphDisplayed] = useState(false);
    const [shouldAutoSelectRows, setShouldAutoSelectRows] = useState(false);

    const {
        actionButtons,
        searchTrackerButtonRef,
        isSearchTrackerPopoverOpen,
        setIsSearchTrackerPopoverOpen,
        ...keywordListSelectionProps
    } = useTableSelectionButtons(setSelectedRows);

    // Handle auto-selection of rows when graph should be displayed
    useEffect(() => {
        if (shouldAutoSelectRows && !isLoading && data.length > 0) {
            const first100Rows = data.slice(0, MAX_DEFAULT_SELECTED_ROWS);
            setSelectedRows(first100Rows);
            setShouldAutoSelectRows(false);
            // Set graph display in next tick after rows are selected
            setTimeout(() => setIsGraphDisplayed(true), 0);
        }
    }, [shouldAutoSelectRows, isLoading, data, setSelectedRows]);

    const onClicksOverTimeButtonClick = useCallback(() => {
        const clicksOverTimeButtonIsOpen = isGraphDisplayed ? "close" : "open";
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Keyword List/Clicks over time button clicked/${clicksOverTimeButtonIsOpen}/${selectedRowsLength}`,
            {
                clicksOverTimeButtonClicked: clicksOverTimeButtonIsOpen,
                selectedKeywordsLength: selectedRowsLength,
            },
        );
        if (isGraphDisplayed) {
            setIsGraphDisplayed(false);
        } else if (!isLoading && data.length > 0) {
            if (!selectedRowsLength) {
                // Need to auto-select rows first, then show graph
                setShouldAutoSelectRows(true);
            } else {
                // Rows already selected, show graph immediately
                setIsGraphDisplayed(true);
            }
        }
    }, [isLoading, selectedRowsLength, data, isGraphDisplayed]);

    const clicksOverTimeButton = [
        {
            type: buttonTypes.FLAT,
            iconName: CLICKS_OVERTIME_ICON,
            children: translate("website.keywords.2.0.table.selection.graph.button"),
            onClick: onClicksOverTimeButtonClick,
            alwaysAvailable: true,
            isDisabled: isLoading || data.length === 0,
        },
    ];

    return {
        actionButtons: [...clicksOverTimeButton, ...actionButtons].filter(Boolean),
        searchTrackerButtonRef,
        isSearchTrackerPopoverOpen,
        isGraphDisplayed,
        setIsGraphDisplayed,
        setIsSearchTrackerPopoverOpen,
        ...keywordListSelectionProps,
    };
};
